import { useEffect, useId, useState } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type ColumnDef, DataTable } from '@/components/common/data-table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  isStaleVersionError,
  ON_FARM_UNKNOWN_STAGE,
  type OnFarmImplantFreshEmbryoEdit,
  type OnFarmImplantFreshEmbryoEditResponse,
  OnFarmImplantRequestError,
  useEditOnFarmImplantFreshEmbryos,
} from '@/services/on-farm-implant';

import {
  type EmbryoEditLockState,
  useEmbryoEditLock,
} from '../../hooks/useEmbryoEditLock';

const STAGES = [4, 5, 6, 7, 8, 9];
const GRADES = [1, 2, 3];

type EditFreshEmbryosFormValues = {
  strawNumber: string;
  stage: string;
  grade: string;
};

const EMPTY_VALUES: EditFreshEmbryosFormValues = {
  strawNumber: '',
  stage: '',
  grade: '',
};

const toNumber = (value: string) => (value ? Number(value) : null);

const sameValues = (
  a: Partial<EditFreshEmbryosFormValues>,
  b: Partial<EditFreshEmbryosFormValues>,
) =>
  a.strawNumber === b.strawNumber && a.stage === b.stage && a.grade === b.grade;

type EditFreshEmbryosDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  sessionEmbryoIds: string[];
  embryoIds: string[];
  strawCount: number;
  onSaved: (updatedCount: number, nextStrawNumber?: number) => void;
};

const renderChange = (
  from: number | null,
  to: number | null,
  emptyLabel = '—',
) =>
  from === to ? (
    (from ?? emptyLabel)
  ) : (
    <span className="font-medium text-blue-700">
      {from ?? emptyLabel} → {to ?? emptyLabel}
    </span>
  );

export function EditFreshEmbryosDialog({
  open,
  onOpenChange,
  ...props
}: EditFreshEmbryosDialogProps) {
  const { t } = useTranslation();
  const [lockAttempt, setLockAttempt] = useState(0);
  // Owned here rather than by the form so the lock follows `open`; mounting it with the form
  // would fire a second, racing acquire whenever React double-invokes mount effects.
  const editLock = useEmbryoEditLock({
    sessionId: props.sessionId,
    embryoIds: props.embryoIds,
    enabled: open,
    attempt: lockAttempt,
  });

  useEffect(() => {
    if (editLock.status === 'timedOut') {
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.fresh.edit.timedOut'),
      });
      onOpenChange(false);
    }
  }, [editLock.status, onOpenChange, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.fresh.edit.title', {
              count: props.sessionEmbryoIds.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.fresh.edit.description')}
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so each opening starts with a fresh form. */}
        <EditFreshEmbryosForm
          {...props}
          editLock={editLock}
          onStale={() => setLockAttempt((attempt) => attempt + 1)}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  );
}

function EditFreshEmbryosForm({
  sessionId,
  sessionEmbryoIds,
  strawCount,
  onSaved,
  onOpenChange,
  editLock,
  onStale,
}: Omit<EditFreshEmbryosDialogProps, 'open'> & {
  editLock: EmbryoEditLockState;
  onStale: () => void;
}) {
  const { t } = useTranslation();
  const formId = useId();
  const { mutateAsync: editEmbryos } = useEditOnFarmImplantFreshEmbryos();

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { disabled, errors, isDirty, isSubmitting },
  } = useForm<EditFreshEmbryosFormValues>({
    defaultValues: EMPTY_VALUES,
    disabled: editLock.status !== 'held',
  });
  const values = useWatch({ control });

  // Keeping the values a preview was built from means any later edit makes it stale without
  // having to clear it from every field's change handler.
  const [previewed, setPreviewed] = useState<{
    values: EditFreshEmbryosFormValues;
    response: OnFarmImplantFreshEmbryoEditResponse;
  } | null>(null);
  const preview =
    previewed && sameValues(previewed.values, values)
      ? previewed.response
      : null;
  const conflicts = preview?.conflictingStrawNumbers ?? [];

  const lockMessage =
    editLock.status === 'acquiring'
      ? t('onFarmImplant.fresh.edit.lockAcquiring')
      : editLock.status === 'conflict'
        ? t('onFarmImplant.fresh.edit.beingEditedBy', {
            names:
              [
                ...new Set(
                  editLock.holders
                    .map((holder) => holder.userDisplayName)
                    .filter(Boolean),
                ),
              ].join(', ') ||
              t('onFarmImplant.fresh.edit.beingEditedBySomeone'),
          })
        : editLock.status === 'lost'
          ? t('onFarmImplant.fresh.edit.lockLost')
          : editLock.status === 'error'
            ? t('onFarmImplant.fresh.edit.lockFailed')
            : null;

  const noChange = t('onFarmImplant.fresh.edit.noChange');
  const stageItems = [
    { value: '', label: noChange },
    ...STAGES.map((value) => ({ value: String(value), label: String(value) })),
    {
      value: String(ON_FARM_UNKNOWN_STAGE),
      label: t('onFarmImplant.fresh.unknownStage'),
    },
  ];
  const gradeItems = [
    { value: '', label: noChange },
    ...GRADES.map((value) => ({ value: String(value), label: String(value) })),
  ];

  const toErrorMessage = (editError: unknown, previewOnly: boolean) => {
    if (isStaleVersionError(editError))
      return t('onFarmImplant.fresh.edit.stale');
    const action = previewOnly ? 'previewFailed' : 'saveFailed';
    if (!(editError instanceof OnFarmImplantRequestError))
      return t(`onFarmImplant.fresh.edit.${action}`);
    if (editError.problems.length) return editError.problems.join(' ');
    if (editError.status === 403)
      return t('onFarmImplant.fresh.edit.forbidden');
    return t(`onFarmImplant.fresh.edit.${action}Status`, {
      status: editError.status,
    });
  };

  const submit =
    (previewOnly: boolean) =>
    async (formValues: EditFreshEmbryosFormValues) => {
      try {
        const response = await editEmbryos({
          sessionId,
          input: {
            sessionEmbryoIds,
            startingStrawNumber: toNumber(formValues.strawNumber),
            stage: toNumber(formValues.stage),
            grade: toNumber(formValues.grade),
            preview: previewOnly,
            lockId: editLock.status === 'held' ? editLock.lockId : null,
            versions:
              editLock.status === 'held' ? editLock.versions : undefined,
          },
        });
        if (previewOnly) {
          setPreviewed({ values: formValues, response });
          return;
        }
        onSaved(
          response.changes.length,
          formValues.strawNumber && strawCount > 0
            ? Number(formValues.strawNumber) + strawCount
            : undefined,
        );
        onOpenChange(false);
      } catch (editError) {
        const message = toErrorMessage(editError, previewOnly);
        toast.add({
          data: { variant: 'error' },
          title: t(
            `onFarmImplant.fresh.edit.${previewOnly ? 'previewFailed' : 'saveFailed'}`,
          ),
          description: message,
        });
        setError('root.server', { message });
        if (isStaleVersionError(editError)) {
          setPreviewed(null);
          onStale();
        }
      }
    };

  const columns: ColumnDef<OnFarmImplantFreshEmbryoEdit>[] = [
    {
      key: 'straw',
      header: t('onFarmImplant.fresh.straw'),
      width: 130,
      render: (change) =>
        renderChange(change.oldStrawNumber, change.newStrawNumber),
    },
    {
      key: 'stage',
      header: t('onFarmImplant.fresh.stage'),
      width: 90,
      render: (change) =>
        renderChange(
          change.oldStage,
          change.newStage,
          t('onFarmImplant.fresh.unknownStage'),
        ),
    },
    {
      key: 'grade',
      header: t('onFarmImplant.fresh.gradeLabel'),
      width: 90,
      render: (change) => renderChange(change.oldGrade, change.newGrade),
    },
    {
      key: 'queueState',
      header: t('onFarmImplant.fresh.status'),
      width: 90,
      render: (change) => change.queueState,
    },
  ];

  const previewedStraw = previewed?.values.strawNumber ?? '';
  const lastPreviewedStraw = Number(previewedStraw) + strawCount - 1;

  return (
    <>
      <DialogBody
        as="form"
        id={formId}
        onSubmit={handleSubmit(submit(true))}
        className="flex flex-col gap-4"
      >
        {lockMessage && (
          <p
            role={editLock.status === 'acquiring' ? 'status' : 'alert'}
            className={
              editLock.status === 'acquiring'
                ? 'text-muted-foreground text-xs'
                : 'text-destructive text-xs'
            }
          >
            {lockMessage}
          </p>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Field>
            <FieldLabel htmlFor={`${formId}-straw`}>
              {t('onFarmImplant.fresh.edit.startingStraw')}
            </FieldLabel>
            <Input
              id={`${formId}-straw`}
              type="number"
              min={1}
              placeholder={noChange}
              {...register('strawNumber')}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${formId}-stage`}>
              {t('onFarmImplant.fresh.stage')}
            </FieldLabel>
            <Controller
              name="stage"
              control={control}
              render={({ field }) => (
                <Select {...field} items={stageItems}>
                  <SelectTrigger id={`${formId}-stage`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stageItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${formId}-grade`}>
              {t('onFarmImplant.fresh.gradeLabel')}
            </FieldLabel>
            <Controller
              name="grade"
              control={control}
              render={({ field }) => (
                <Select {...field} items={gradeItems}>
                  <SelectTrigger id={`${formId}-grade`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {gradeItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </div>

        {values.strawNumber && (
          <p className="text-muted-foreground text-xs">
            {t('onFarmImplant.fresh.edit.strawHint')}
          </p>
        )}

        {preview && (
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold text-gray-600">
              {t('onFarmImplant.fresh.edit.affected', {
                count: preview.changes.length,
              })}
            </h3>
            <DataTable
              rows={preview.changes}
              columns={columns}
              getRowId={(change) => change.sessionEmbryoId}
              storageKey="on-farm-implant-fresh-edit-preview"
              headerClassName="font-['Poppins',sans-serif] font-medium tracking-normal normal-case"
              fillWidth
            />
          </div>
        )}

        {conflicts.length > 0 && (
          <p role="alert" className="text-destructive text-xs">
            {t('onFarmImplant.fresh.edit.conflicts', {
              range:
                strawCount > 1
                  ? `${previewedStraw}-${lastPreviewedStraw}`
                  : previewedStraw,
              straws: conflicts.join(', '),
            })}
          </p>
        )}
        {errors.root?.server && (
          <p role="alert" className="text-destructive text-xs">
            {errors.root.server.message}
          </p>
        )}
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
        >
          {t('onFarmImplant.fresh.edit.cancel')}
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="outline"
          disabled={disabled || isSubmitting || !isDirty}
        >
          {t('onFarmImplant.fresh.edit.preview')}
        </Button>
        <Button
          type="button"
          disabled={
            disabled || isSubmitting || !preview || conflicts.length > 0
          }
          onClick={handleSubmit(submit(false))}
        >
          {t('onFarmImplant.fresh.edit.save')}
        </Button>
      </DialogFooter>
    </>
  );
}
